import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Collapse,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";

import DoneIcon from "@mui/icons-material/Done";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplyIcon from "@mui/icons-material/Reply";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import AttachmentPreview from "../preview/AttachmentPreview";

function ActionIconButton({ title, onClick, icon, sx = {}, active = false }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          height: 30,
          width: 30,
          border: "1px solid #e2e8f0",
          bgcolor: active ? "#e0e7ff" : "#f8fafc",
          color: active ? "#1d4ed8" : "#475569",
          transition: "all 0.15s ease",
          transform: "translateY(0)",
          "&:hover": {
            bgcolor: active ? "#c7d2fe" : "#eef2ff",
            color: "#1d4ed8",
            borderColor: "#c7d2fe",
            transform: "translateY(-1px)",
            boxShadow: "0 8px 16px rgba(29, 78, 216, 0.12)",
          },
          "&:active": {
            transform: "translateY(0)",
            boxShadow: "0 2px 6px rgba(15, 23, 42, 0.15)",
          },
          ...sx,
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

/* ----------------------------------------------------------
   HTML correo: iframe + sanitizado (conservamos <style>; antes se borraban y correos tipo Cloudflare quedaban rotos)
---------------------------------------------------------- */
function sanitizeEmailFragment(html) {
  return String(html || "")
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<meta[^>]*http-equiv\s*=\s*["']?refresh["']?[^>]*>/gi, "")
    .replace(/on\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/on\w+\s*=\s*'[^']*'/gi, "")
    .replace(/--matrixboundary--/g, "")
    .replace(/<\/script/gi, "<\\/script");
}

function buildEmailSrcDoc(fragment) {
  const inner = sanitizeEmailFragment(fragment);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank" rel="noopener noreferrer"></head><body style="margin:0;padding:12px;background:#fff;overflow-x:auto;">${inner}</body></html>`;
}

function EmailBodyIframe({ html }) {
  const iframeRef = useRef(null);
  const srcDoc = useMemo(() => buildEmailSrcDoc(html), [html]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        const body = doc?.body;
        const root = doc?.documentElement;
        if (!body) return;
        const h = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          root?.scrollHeight ?? 0,
          root?.offsetHeight ?? 0,
          80
        );
        iframe.style.height = `${h + 16}px`;
      } catch {
        iframe.style.height = "400px";
      }
    };

    const onLoad = () => {
      resize();
      requestAnimationFrame(resize);
      [50, 200, 600].forEach((ms) => setTimeout(resize, ms));
      try {
        iframe.contentDocument?.querySelectorAll?.("img").forEach((img) => {
          img.addEventListener("load", resize);
        });
      } catch {
        /* ignore */
      }
    };

    iframe.addEventListener("load", onLoad);
    return () => iframe.removeEventListener("load", onLoad);
  }, [srcDoc]);

  return (
    <iframe
      ref={iframeRef}
      title="Cuerpo del mensaje"
      srcDoc={srcDoc}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      style={{
        width: "100%",
        border: 0,
        display: "block",
        minHeight: 120,
        backgroundColor: "#fff",
      }}
    />
  );
}

/* ==========================================================
   BUBBLE WHATSAPP (todo abierto)
========================================================== */
function BubbleWhatsApp({
  email,
  body,
  loadingBody,
  attachments,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  isOutgoing,
}) {
  const bubbleColor = isOutgoing ? "#dcfce7" : "#e0e7ff";
  const accentColor = isOutgoing ? "#ffae47" : "#69cbf9";

  return (
    <Fade in>
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            borderRadius: isOutgoing
              ? "16px 16px 4px 16px"
              : "16px 16px 16px 4px",
            p: 2,
            border: "1px solid #e2e8f0",
            borderLeft: `1.5px solid ${accentColor}`,
            backgroundColor: "#ffffff",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                {email.from}
              </Typography>

              <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                {new Date(email.date).toLocaleString("es-ES")}
              </Typography>
            </Box>

            {loadingBody ? <CircularProgress size={22} /> : null}
            <Collapse in={!loadingBody} timeout={220} unmountOnExit>
              <Box
                sx={{
                  backgroundColor: bubbleColor,
                  borderRadius: "10px",
                  padding: "10px 12px",
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <EmailBodyIframe html={body} />
              </Box>
              {attachments?.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {attachments.map((a) => (
                    <AttachmentPreview key={a.id} file={a} />
                  ))}
                </Box>
              )}
            </Collapse>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              ml: 1,
              gap: 0.7,
              alignItems: "flex-start",
            }}
          >
            <ActionIconButton
              title={email.isRead ? "Marcar como no leído" : "Marcar como leído"}
              onClick={() => toggleRead(email.gmailId)}
              active={!email.isRead}
              icon={
                email.isRead ? (
                  <MailOutlineIcon sx={{ fontSize: "1rem" }} />
                ) : (
                  <DoneIcon sx={{ fontSize: "1rem", color: "#0284c7" }} />
                )
              }
            />

            <ActionIconButton
              title="Responder"
              onClick={() => openReply(email)}
              icon={<ReplyIcon sx={{ fontSize: "1rem" }} />}
            />

            <ActionIconButton
              title="Reenviar"
              onClick={() => openForward(email)}
              icon={<ReplyIcon sx={{ fontSize: "1rem", transform: "scaleX(-1)" }} />}
            />

            <ActionIconButton
              title="Eliminar"
              onClick={() => askDelete(email.gmailId)}
              icon={<DeleteIcon sx={{ fontSize: "1rem", color: "#dc2626" }} />}
              sx={{
                "&:hover": {
                  bgcolor: "#fee2e2",
                  borderColor: "#fecaca",
                  color: "#b91c1c",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

/* ==========================================================
   BUBBLE EMAIL (cerrado + se abre al clic)
========================================================== */
function BubbleEmail({
  email,
  selectedEmail,
  onSelect,
  body,
  loadingBody,
  attachments,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  getAccountColor,
  selectionMode,
  selected,
  toggleSelectEmail,
}) {
  const bubbleColor = email.direction === "sent" ? "#dcfce7" : "#e0e7ff";
  const isOpen = selectedEmail?.gmailId === email.gmailId;
  const accentColor = email.direction === "sent" ? "#ffae47" : "#69cbf9";
  const compactChipSx = {
    height: 22,
    "& .MuiChip-label": {
      px: 1,
      fontSize: 11,
    },
  };

  return (
    <Fade in>
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
          }}
        >
          {/* BURBUJA */}
          <Box sx={{ flex: 1, minWidth: 0, width: "auto" }}>
            <Box
              onClick={() => !selectionMode && onSelect(email)}
              sx={{
                display: "flex",
                width: "auto",
                maxWidth: "100%",
                bgcolor: "#ffffff",
                borderRadius: isOpen ? "12px 12px 0 0" : "12px",
                p: 2,
                cursor: selectionMode ? "default" : "pointer",
                border: "1px solid #e2e8f0",
                borderLeft: `1.5px solid ${accentColor}`,
                borderBottom: isOpen ? "0" : "1px solid #e2e8f0",
                boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
                transition: "0.15s",
                "&:hover": { boxShadow: "0 12px 26px rgba(15, 23, 42, 0.14)" },
                position: "relative",
                overflow: "hidden",
                
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Chip
                  label={email.direction === "sent" ? "Enviado" : "Recibido"}
                  size="small"
                  sx={{
                    mb: 1,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: bubbleColor,
                    color: "#0f172a",
                  }}
                />
                {/* Fecha */}
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  {new Date(email.date).toLocaleString("es-ES")}
                </Typography>

                {/* CABECERA ESTILO OUTLOOK */}
                <Box sx={{ mb: 0.8, display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    Asunto:
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                    {email.subject}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mb: 0.8,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    De:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                    <Chip
                      label={email.from}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={compactChipSx}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    mb: 0.8,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    Para:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                    {email.to_email?.split(",").map((t) => (
                      <Chip
                        key={t.trim()}
                        label={t.trim()}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ ...compactChipSx, background: "#e7f3ff" }}
                      />
                    ))}
                  </Box>
                </Box>

                {email.cc_email && (
                  <Box
                    sx={{
                      mb: 0.8,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                      CC:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                      {email.cc_email?.split(",").map((c) => (
                        <Chip
                          key={c.trim()}
                          label={c.trim()}
                          size="small"
                          sx={{ ...compactChipSx, background: "#faf0c4" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {email.bcc_email && (
                  <Box sx={{ mb: 0.8, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 0.4 }}>
                      BCC:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                      {email.bcc_email?.split(",").map((b) => (
                        <Chip
                          key={b.trim()}
                          label={b.trim()}
                          size="small"
                          sx={{ ...compactChipSx, background: "#e5e5e5" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Chips automáticos */}
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {email.snippet?.includes("factura") && (
                    <Chip
                      label="Facturación"
                      size="small"
                      sx={{ backgroundColor: "#69f9ce8e", color: "#444" }}
                    />
                  )}

                  {email.hasAttachments && (
                    <Chip
                      label="Adjuntos"
                      size="small"
                      sx={{ backgroundColor: "#69cbf9", color: "#444" }}
                    />
                  )}

                  {email.from?.includes("@gmail.com") && (
                    <Chip label="Personal" size="small" variant="outlined" />
                  )}
                </Box>

                {/* Snippet */}
                {!isOpen && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#334155",
                      mt: 1,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {email.snippet}
                  </Typography>
                )}
              </Box>

              {/* BOTONES */}
              <Box
                sx={{
                  display: "flex",
                  width: "fit-content",
                  ml: 1,
                  gap: 0.7,
                  alignItems: "flex-start",
                }}
              >
                <ActionIconButton
                  title={selected ? "Quitar selección" : "Seleccionar"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelectEmail(email.gmailId);
                  }}
                  active={selected}
                  icon={
                    selected ? (
                      <CheckBoxIcon sx={{ fontSize: "1rem", color: "#2563eb" }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem" }} />
                    )
                  }
                />

                <ActionIconButton
                  title={email.isRead ? "Marcar como no leído" : "Marcar como leído"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRead(email.gmailId);
                  }}
                  active={!email.isRead}
                  icon={
                    email.isRead ? (
                      <MailOutlineIcon sx={{ fontSize: "1rem" }} />
                    ) : (
                      <DoneIcon sx={{ fontSize: "1rem", color: "#0284c7" }} />
                    )
                  }
                />

                <ActionIconButton
                  title="Responder"
                  onClick={(e) => {
                    e.stopPropagation();
                    openReply(email);
                  }}
                  icon={<ReplyIcon sx={{ fontSize: "1rem" }} />}
                />

                <ActionIconButton
                  title="Reenviar"
                  onClick={(e) => {
                    e.stopPropagation();
                    openForward(email);
                  }}
                  icon={<ReplyIcon sx={{ fontSize: "1rem", transform: "scaleX(-1)" }} />}
                />

                <ActionIconButton
                  title="Eliminar"
                  onClick={(e) => {
                    e.stopPropagation();
                    askDelete(email.gmailId);
                  }}
                  icon={<DeleteIcon sx={{ fontSize: "1rem", color: "#dc2626" }} />}
                  sx={{
                    "&:hover": {
                      bgcolor: "#fee2e2",
                      borderColor: "#fecaca",
                      color: "#b91c1c",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* PANEL EXPANDIDO */}
            <Collapse in={isOpen} timeout={220} unmountOnExit>
              <Box
                sx={{
                  mt: 0,
                  width: "100%",
                  bgcolor: "white",
                  borderRadius: "0 0 12px 12px",
                  p: 2,
                  border: "1px solid #dbe3ef",
                  borderTop: "0",
                  borderLeft: `1.5px solid ${accentColor}`,
                  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
                  overflow: "hidden",
                }}
              >
                {loadingBody ? (
                  <CircularProgress size={22} />
                ) : (
                  <EmailBodyIframe html={body} />
                )}

                {/* Adjuntos */}
                {attachments?.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {attachments.map((a) => (
                      <AttachmentPreview key={a.id} file={a} />
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

/* ==========================================================
   PANEL DERECHO COMPLETO
========================================================== */
export default function EmailPanelDerecho({
  threads,
  selectedPerson,
  filter = "all",
  modoVista,
  bodyCache,
  attachmentsByEmail,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  loadBodyCached,
  loadAttachments,
  getAccountColor,
  selectionMode,
  selectionBarMode,
  selectedEmails,
  toggleSelectEmail,
  clearSelection,
}) {
  const [selectedEmail, setSelectedEmail] = useState(null);

  const currentThread = useMemo(() => {
    const raw = threads[selectedPerson];
    const base = Array.isArray(raw) ? raw : [];
    let list = [...base];
    if (filter === "read") list = list.filter((e) => e.isRead);
    if (filter === "unread") list = list.filter((e) => !e.isRead);
    return list;
  }, [threads, selectedPerson, filter]);

  const currentContact = selectedPerson
    ? selectedPerson.split("::")[1] || selectedPerson
    : "";

  /* Si el filtro oculta el mensaje abierto, cerrar la burbuja */
  useEffect(() => {
    const ids = new Set(currentThread.map((e) => e.gmailId));
    setSelectedEmail((prev) => (prev && ids.has(prev.gmailId) ? prev : null));
  }, [currentThread]);

  /* Cargar cuerpos + adjuntos (toda la conversación, para al cambiar filtro ya estén en caché) */
  useEffect(() => {
    if (!selectedPerson) return;
    const mails = threads[selectedPerson] || [];
    mails.forEach((m) => {
      loadBodyCached(m.gmailId);
      loadAttachments(m.gmailId);
    });
  }, [selectedPerson, threads, loadBodyCached, loadAttachments]);

  if (!selectedPerson) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          color: "#64748b",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
            Selecciona una conversacion
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
            Elige un contacto para ver el historial.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        p: 3.5,
        bgcolor: "#f8fafc",
        pt: selectionMode && selectionBarMode === "top" ? 7 : 3,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          mb: 2,
          p: 0.25,
          /* border: "1px solid #dbe3ef", */
          borderRadius: 1,
         /*  backgroundColor: "#cacacad9", */
          backdropFilter: "blur(6px)",
         /*  boxShadow: "0 6px 14px rgba(15,23,42,0.06)", */
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
          {currentContact}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
          {filter === "unread" &&
            `${currentThread.length} no leídos en esta conversación`}
          {filter === "read" &&
            `${currentThread.length} leídos en esta conversación`}
          {filter === "all" &&
            `${currentThread.length} mensajes en la conversacion`}
        </Typography>
      </Box>

      {currentThread.map((email) => {
        const body = bodyCache[email.gmailId] || "";
        const loading = !bodyCache[email.gmailId];
        const attachments = attachmentsByEmail[email.gmailId] || [];

        return modoVista === "whatsapp" ? (
          <BubbleWhatsApp
            key={email.gmailId}
            email={email}
            body={body}
            loadingBody={loading}
            attachments={attachments}
            toggleRead={toggleRead}
            openReply={openReply}
            openForward={openForward}
            askDelete={askDelete}
            isOutgoing={email.direction === "sent"}
          />
        ) : (
          <BubbleEmail
            key={email.gmailId}
            email={email}
            selectedEmail={selectedEmail}
            onSelect={(email) => {
              if (selectionMode) return;

              setSelectedEmail((prev) =>
                prev?.gmailId === email.gmailId ? null : email
              );

              // 🔥 SOLUCIÓN: Cargar body y adjuntos si aún no están
              loadBodyCached(email.gmailId);
              loadAttachments(email.gmailId);
            }}
            body={body}
            loadingBody={loading}
            attachments={attachments}
            toggleRead={toggleRead}
            openReply={openReply}
            openForward={openForward}
            askDelete={askDelete}
            getAccountColor={getAccountColor}
            selectionMode={selectionMode}
            selected={selectedEmails.includes(email.gmailId)}
            toggleSelectEmail={toggleSelectEmail}
          />
        );
      })}
    </Box>
  );
}
